import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, Smartphone } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { resolveClipCoverImage } from '@/lib/cover/coverImage';
import { LayoutStillThumb } from '@/components/layouts/LayoutStillThumb';
import {
  applyLayoutBinds,
  resolveClipTitleForBind,
  type LayoutFieldBinds,
} from '@/lib/layouts/layoutFieldBinds';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import { cn } from '@/lib/utils';

export type MassExportFormat = 'horizontal' | 'vertical';

export interface MassExportPreviewClip {
  id: string | number;
  title?: string;
  clip_thumbnail?: string | null;
  thumbnail_url?: string | null;
  finished_video_thumbnail_url?: string | null;
  clip_start_time?: number;
  clip_end_time?: number;
  data?: unknown;
  skippedClipId?: string | null;
  activeClipId?: string | null;
}

interface MassExportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  clips: MassExportPreviewClip[];
  focusedClipId: string | number;
  onFocusClip: (id: string | number) => void;
  postLayout: PostLayoutConfig | null;
  fieldBinds: LayoutFieldBinds;
  channelName?: string;
  channelAvatarUrl?: string;
  userName?: string;
  userAvatarUrl?: string;
  availableFormats: MassExportFormat[];
  selectedFormat: MassExportFormat;
  onFormatChange: (format: MassExportFormat) => void;
  livePreview: ReactNode;
  onExport: (format: MassExportFormat) => void;
}

export function MassExportPreviewModal({
  isOpen,
  onClose,
  clips,
  focusedClipId,
  onFocusClip,
  postLayout,
  fieldBinds,
  channelName,
  channelAvatarUrl,
  userName,
  userAvatarUrl,
  availableFormats,
  selectedFormat,
  onFormatChange,
  livePreview,
  onExport,
}: MassExportPreviewModalProps) {
  const { t } = useTranslation();
  const exportFormat = availableFormats.includes(selectedFormat)
    ? selectedFormat
    : (availableFormats[0] ?? 'vertical');
  const thumbAspect = exportFormat === 'horizontal' ? 'landscape' : 'portrait';
  const clipKey = clips.map((clip) => String(clip.id)).join(',');
  const [readyCount, setReadyCount] = useState(1);
  const settledIndexesRef = useRef(new Set<number>());

  useEffect(() => {
    if (!isOpen) return;
    settledIndexesRef.current = new Set();
    setReadyCount(1);
  }, [isOpen, clipKey, thumbAspect]);

  const handleThumbSettled = useCallback((index: number) => {
    settledIndexesRef.current.add(index);
    setReadyCount((count) => {
      // Include a focused-ahead settle so later inSequence does not wait
      // for a Thumbnail settle that already fired.
      let next = Math.max(count, index + 1);
      // Unfocused thumbs still enqueue one extra at a time. Walk only
      // through indexes that have already settled.
      while (next < clips.length && settledIndexesRef.current.has(next - 1)) {
        next += 1;
      }
      return next;
    });
  }, [clips.length]);

  const thumbItems = useMemo(
    () => clips.map((clip) => ({
      clip,
      stillUrl:
        clip.clip_thumbnail ||
        clip.thumbnail_url ||
        clip.finished_video_thumbnail_url ||
        null,
      // Each clip exports with its own clip.data.coverImage.
      hasCover: resolveClipCoverImage({ clipData: clip.data }) !== null,
      layout: postLayout
        ? applyLayoutBinds(postLayout, fieldBinds, {
            clipTitle: resolveClipTitleForBind(clip.title),
            channelName,
            channelAvatarUrl,
            userName,
            userAvatarUrl,
          })
        : null,
    })),
    [clips, postLayout, fieldBinds, channelName, channelAvatarUrl, userName, userAvatarUrl],
  );

  const focusedIndex = Math.max(
    0,
    clips.findIndex((clip) => clip.id === focusedClipId),
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        data-testid="mass-export-preview-modal"
        className="max-w-6xl w-[95vw] sm:w-[90vw] max-h-[90vh] overflow-hidden flex flex-col"
      >
        <DialogHeader className="flex-shrink-0 space-y-1">
          <DialogTitle>
            {t('projectEditor.massExportPreview.title', { count: clips.length })}
          </DialogTitle>
          <DialogDescription>
            {clips.length > 0
              ? t('projectEditor.massExportPreview.clipOf', {
                  current: focusedIndex + 1,
                  count: clips.length,
                })
              : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 gap-3 sm:gap-4">
          <div
            className={cn(
              'min-w-0 flex-1 flex items-center justify-center overflow-hidden rounded-lg bg-black',
              thumbAspect === 'portrait' ? 'min-h-[280px] sm:min-h-[360px]' : 'min-h-[200px] sm:min-h-[280px]',
            )}
          >
            <div
              className={cn(
                'h-full max-h-[60vh] w-full',
                thumbAspect === 'portrait' ? 'max-w-[min(100%,calc(60vh*9/16))]' : 'max-w-full',
              )}
            >
              {livePreview ?? <div className="h-full w-full bg-muted" />}
            </div>
          </div>

          <div className="w-20 sm:w-24 flex-shrink-0 overflow-y-auto space-y-2 pr-0.5">
            {thumbItems.map(({ clip, stillUrl, layout, hasCover }, index) => {
              const focused = clip.id === focusedClipId;
              const inSequence = index < readyCount;
              return (
                <button
                  key={clip.id}
                  type="button"
                  data-testid={`mass-export-thumb-${clip.id}`}
                  aria-pressed={focused}
                  title={clip.title}
                  onClick={() => onFocusClip(clip.id)}
                  className={cn(
                    'relative block w-full overflow-hidden rounded-md transition-shadow',
                    focused
                      ? 'z-10 ring-4 ring-primary ring-offset-2 ring-offset-black'
                      : 'ring-2 ring-transparent hover:ring-muted-foreground/50',
                  )}
                >
                  <LayoutStillThumb
                    layout={layout}
                    stillUrl={stillUrl}
                    aspect={thumbAspect}
                    mountRenderer={inSequence || focused}
                    onRendererSettled={
                      inSequence || focused
                        ? () => handleThumbSettled(index)
                        : undefined
                    }
                  />
                  {hasCover && (
                    <Badge
                      variant="secondary"
                      data-testid={`mass-export-cover-badge-${clip.id}`}
                      className="pointer-events-none absolute left-1 top-1 px-1.5 py-0 text-[10px]"
                    >
                      {t('editor.coverImage.badge')}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex gap-2">
            {availableFormats.includes('horizontal') && (
              <Button
                type="button"
                variant={exportFormat === 'horizontal' ? 'default' : 'outline'}
                onClick={() => onFormatChange('horizontal')}
              >
                <Monitor className="w-4 h-4 mr-2" />
                {t('exportModal.horizontal')}
              </Button>
            )}
            {availableFormats.includes('vertical') && (
              <Button
                type="button"
                variant={exportFormat === 'vertical' ? 'default' : 'outline'}
                onClick={() => onFormatChange('vertical')}
              >
                <Smartphone className="w-4 h-4 mr-2" />
                {t('exportModal.vertical')}
              </Button>
            )}
          </div>
          <Button
            type="button"
            onClick={() => {
              onClose();
              onExport(exportFormat);
            }}
          >
            {t('projectEditor.massExportPreview.exportN', { count: clips.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MassExportPreviewModal;
